// ── ADMIN: STUDENTS (performer roster + performer detail) ────────────
// billingSwitchModal/dropModal/enrollProgramModal/childModal
// (admin-student-actions.js) are real imports now, wired via
// VIEW_INIT['a-performer-detail'] below — renderStudentTable() is also
// still called directly (not via onclick) from that module's saveChild(),
// exported here for that same real import.
//
// stProgram/stPreserveFilter are read/written by goToPerformers() in
// admin-dashboard.js via real imports (setStProgram/setStPreserveFilter) —
// exported here, no window exposure needed since nothing reaches them via
// onclick="".
//
// stSetSort/stSetSearch/stClearSearch/stSetProgram all rewrite #stTableWrap
// (via renderStudentTable()) directly, bypassing route()'s VIEW_INIT
// lifecycle — wireStudentTable() is the shared re-wire step renderStudentTable()
// itself calls at the end, since every path that changes the table goes
// through it. VIEW_INIT['a-students'] wires the search/clear/program filter
// controls (which don't get rewritten by renderStudentTable()) once per real
// page render.
import { DB, PROGRAMS, session, currentPerformerId } from '../core/state.js';
import { kidProgramNames, acctName, kidInProgram, activePrograms, canEdit, kidPrograms, activeEnrollment, enrollmentBillingMode, isArchived } from '../core/domain-utils.js';
import { VIEWS, VIEW_INIT, route } from '../core/routing.js';
import { priceLabel, enrollmentPriceLabel } from './billing-shared.js';
import { childModal, enrollProgramModal, billingSwitchModal, dropModal } from './admin-student-actions.js';
import { archivePerformerModal, unarchivePerformer, archivedPill, archivedBanner } from './admin-archive.js';

let stSort = { col: 'name', dir: 'asc' };
let stSearch = '';
// The roster is the view that made archiving worth building: without this
// filter a performer who left two seasons ago sits in "All performers"
// forever. Default 'current' — archived ones are one dropdown away, never
// gone.
let stArchived = 'current';
export let stProgram = '';
export function setStProgram(v) { stProgram = v; }
let stPreserveFilter = false;
export function setStPreserveFilter(v) { stPreserveFilter = v; }

function stSorted() {
  let rows = DB.children.map(k => ({
    ...k,
    age: calcAge(k.dob),
    programName: kidProgramNames(k),
    accountName: acctName(k.accountId)
  }));
  // filter
  if (stArchived === 'current')  rows = rows.filter(r => !isArchived(r));
  if (stArchived === 'archived') rows = rows.filter(r => isArchived(r));
  if (stProgram) rows = rows.filter(r => kidInProgram(r, stProgram));
  if (stSearch) {
    const q = stSearch.toLowerCase();
    rows = rows.filter(r =>
      r.name.toLowerCase().includes(q) ||
      r.programName.toLowerCase().includes(q) ||
      r.accountName.toLowerCase().includes(q) ||
      (r.school || '').toLowerCase().includes(q) ||
      (r.grade || '').toLowerCase().includes(q)
    );
  }
  // sort
  rows.sort((a, b) => {
    let av = a[stSort.col] ?? '', bv = b[stSort.col] ?? '';
    if (stSort.col === 'age') { av = +av; bv = +bv; }
    else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
    return stSort.dir === 'asc' ? (av > bv ? 1 : av < bv ? -1 : 0) : (av < bv ? 1 : av > bv ? -1 : 0);
  });
  return rows;
}
// Both filters feed the heading, and both can change without a full page
// render (stSetProgram/stSetArchived only rewrite the table), so the label is a
// function that renderStudentTable() re-runs rather than a value baked into the
// view's HTML once.
function stCountLabel() {
  const archivedCount = DB.children.filter(isArchived).length;
  const shown = stSorted().length;
  const program = stProgram ? (PROGRAMS.find(p => p.id === stProgram) || {}).name || 'Program' : '';

  const head = program
    ? `${program} performers (${shown})`
    : stArchived === 'archived' ? `Archived performers (${shown})`
    : stArchived === 'all'      ? `All performers, including archived (${shown})`
    : `All performers (${shown})`;

  // The archived tally is a pointer to the other filter, so it's noise once
  // you're already looking at them.
  const tail = archivedCount && stArchived === 'current'
    ? ` <span style="color:var(--muted);font-weight:400">· ${archivedCount} archived</span>`
    : '';

  return head + tail;
}

function stSetSort(col) {
  if (stSort.col === col) stSort.dir = stSort.dir === 'asc' ? 'desc' : 'asc';
  else { stSort.col = col; stSort.dir = 'asc'; }
  renderStudentTable();
}
function stSetSearch(val) {
  stSearch = val;
  const btn = document.getElementById('stClearBtn');
  if (btn) btn.style.display = val ? 'block' : 'none';
  renderStudentTable();
}
function stClearSearch() {
  stSearch = '';
  const inp = document.getElementById('stSearchInput');
  if (inp) { inp.value = ''; inp.focus(); }
  const btn = document.getElementById('stClearBtn');
  if (btn) btn.style.display = 'none';
  renderStudentTable();
}
function stSetProgram(val) { stProgram = val; renderStudentTable(); }
function stSetArchived(val) { stArchived = val; renderStudentTable(); }

export function renderStudentTable() {
  const rows = stSorted();
  const label = document.getElementById('stCountLabel');
  if (label) label.innerHTML = stCountLabel();
  const th = (col, label) => {
    const cls = stSort.col === col ? stSort.dir : '';
    return `<th class="sort-th ${cls}" data-action="st-set-sort" data-col="${col}">${label}<span class="sort-arrow"></span></th>`;
  };
  document.getElementById('stTableWrap').innerHTML = `
    <div class="tbl-wrap"><table>
      <thead><tr>
        ${th('name','Performer')}
        ${th('age','Age')}
        ${th('grade','Grade')}
        ${th('school','School')}
        ${th('programName','Program')}
        ${th('accountName','Account')}
      </tr></thead>
      <tbody>
        ${rows.length ? rows.map(k => `<tr>
          <td>${canEdit(session) ? `<button class="tbl-link" onclick="viewPerformer('${k.id}')">${esc(k.name)}</button>` : esc(k.name)}${isArchived(k) ? ' ' + archivedPill() : ''}</td>
          <td>${k.age}</td>
          <td>${k.grade ? esc(k.grade) : '—'}</td>
          <td style="color:var(--muted)">${k.school ? esc(k.school) : '—'}</td>
          <td>${esc(k.programName)}</td>
          <td><button class="tbl-link" onclick="viewParent('${k.accountId}')">${esc(k.accountName)}</button></td>
        </tr>`).join('') : `<tr><td colspan="6" style="padding:20px 12px"><p class="empty" style="padding:0">No performers match these filters.</p></td></tr>`}
      </tbody>
    </table></div>`;
  wireStudentTable();
}
function wireStudentTable() {
  const wrap = document.getElementById('stTableWrap');
  if (!wrap) return;
  wrap.querySelectorAll('[data-action="st-set-sort"]').forEach(th => {
    th.addEventListener('click', () => stSetSort(th.dataset.col));
  });
}

VIEWS['a-students'] = () => {
  if (!_routePushState) { /* background refresh — leave filter state alone */ }
  else if (stPreserveFilter) { stPreserveFilter = false; stSearch = ''; }
  else { stSearch = ''; stProgram = ''; stArchived = 'current'; }

  const html = `
    <div class="page-head">
      <h2 class="serif">Performers</h2>
    </div>
    <div class="card">
      <div class="card-hd">
        <span id="stCountLabel">${stCountLabel()}</span>
      </div>
      <div class="filters" style="margin-bottom:16px">
        <div class="field" style="flex:1;min-width:200px;margin-bottom:0">
          <label>Search</label>
          <div style="position:relative">
            <input type="text" id="stSearchInput" placeholder="Name, program, or account…" data-action="st-search-input" value="${esc(stSearch)}" style="height:38px;padding-right:28px;width:100%;box-sizing:border-box">
            <button id="stClearBtn" data-action="st-clear-search" aria-label="Clear search" style="position:absolute;right:6px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--muted);font-size:18px;line-height:1;padding:2px 4px;display:${stSearch ? 'block' : 'none'}">×</button>
          </div>
        </div>
        <div class="field" style="min-width:180px;margin-bottom:0">
          <label>Program</label>
          <select id="stProgSelect" data-action="st-set-program" style="height:38px">
            <option value="">All programs</option>
            ${activePrograms().map(p => `<option value="${p.id}" ${stProgram === p.id ? 'selected' : ''}>${esc(p.name)}</option>`).join('')}
          </select>
        </div>
        <div class="field" style="min-width:150px;margin-bottom:0">
          <label>Show</label>
          <select id="stArchivedSelect" style="height:38px">
            <option value="current" ${stArchived === 'current' ? 'selected' : ''}>Current</option>
            <option value="archived" ${stArchived === 'archived' ? 'selected' : ''}>Archived</option>
            <option value="all" ${stArchived === 'all' ? 'selected' : ''}>All</option>
          </select>
        </div>
      </div>
      <div id="stTableWrap"></div>
    </div>`;
  // render table after DOM settles
  setTimeout(renderStudentTable, 0);
  return html;
};
VIEW_INIT['a-students'] = () => {
  const searchEl = document.getElementById('stSearchInput');
  if (searchEl) searchEl.addEventListener('input', () => stSetSearch(searchEl.value));
  const clearBtn = document.getElementById('stClearBtn');
  if (clearBtn) clearBtn.addEventListener('click', stClearSearch);
  const progSel = document.getElementById('stProgSelect');
  if (progSel) progSel.addEventListener('change', () => stSetProgram(progSel.value));
  const archSel = document.getElementById('stArchivedSelect');
  if (archSel) archSel.addEventListener('change', () => stSetArchived(archSel.value));
};

VIEWS['a-performer-detail'] = () => {
  const ch = DB.children.find(c => c.id === currentPerformerId);
  if (!ch) { route('a-students'); return ''; }
  const acct = DB.users.find(u => u.id === ch.accountId);
  const age = calcAge(ch.dob);
  const enrolledPrograms = kidPrograms(ch).map(id => PROGRAMS.find(p => p.id === id)).filter(Boolean);
  const editable = canEdit(session);
  // Enrolling, dropping, or switching billing on someone who's been filed away
  // is incoherent -- the banner above tells you to restore them first, and
  // hiding the buttons is what makes that instruction true rather than advice.
  const enrollable = editable && !isArchived(ch);

  // Eligible programs the performer is NOT already in (for the Enroll action)
  const addable = activePrograms().filter(p =>
    !enrolledPrograms.some(e => e.id === p.id) &&
    !(age !== null && ((p.ageMin !== null && age < p.ageMin) || (p.ageMax !== null && age > p.ageMax)))
  );

  const enrollmentCard = p => {
    const isProg = p.isProgram;
    const se = isProg ? activeEnrollment(ch.id, p.id) : null;
    const mode = isProg ? enrollmentBillingMode(ch.id, p.id) : 'onetime';
    const modeLabel = !isProg ? 'One-time' : (mode === 'comp' ? 'Comp' : mode === 'advance' ? 'Pay in advance' : 'Monthly');
    // A reduced rate is set by hand (scripts/rate_override.php) and is invisible
    // everywhere else, so it is named here rather than just quietly replacing
    // the number -- otherwise this page looks like it disagrees with the catalog.
    const reduced = se && se.rateOverride != null;
    const rateLabel = reduced ? enrollmentPriceLabel(p, ch.id) : priceLabel(p);
    const reducedNote = reduced
      ? ` · <span style="color:var(--gold)">reduced rate</span> <span style="opacity:.6">(normally ${priceLabel(p)})</span>`
      : '';
    return `
      <div style="border:1px solid var(--border-soft);border-radius:10px;padding:16px;margin-bottom:12px">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap">
          <div>
            <div style="font-size:14px;color:var(--cream)">${esc(p.name)}</div>
            <div style="font-size:12px;color:var(--muted);margin-top:6px">${rateLabel} · ${modeLabel}${reducedNote}${isProg && se ? '' : (isProg ? ' · not in current season' : '')}</div>
          </div>
          ${enrollable ? `<div style="display:flex;gap:8px;flex-shrink:0">
            ${isProg && se && p.allowAdvance && mode !== 'comp' ? `<button class="btn-ghost btn-sm" data-action="billing-switch" data-child="${ch.id}" data-program="${p.id}">Change billing</button>` : ''}
            ${isProg ? `<button class="btn-ghost btn-sm btn-danger" data-action="drop-program" data-child="${ch.id}" data-program="${p.id}">Drop</button>` : ''}
          </div>` : ''}
        </div>
      </div>`;
  };

  return `
    <div class="page-head">
      <span class="eyebrow check-row" onclick="route('a-students')">‹ Performers</span>
      <h2 class="serif">${esc(ch.firstName)} <em>${esc(ch.lastName)}</em></h2>
    </div>

    ${isArchived(ch) ? archivedBanner(ch, { kind: 'performer', onRestoreAction: 'unarchive-performer' }) : ''}

    <div class="grid-2" style="align-items:start">
      <div style="display:flex;flex-direction:column;gap:18px">
        <div class="card">
          <div class="card-hd">Performer info ${editable ? `<span style="display:inline-flex;gap:8px">
            <button class="btn-ghost btn-sm" data-action="edit-child" data-child="${ch.id}">Edit</button>
            ${isArchived(ch) ? '' : `<button class="btn-ghost btn-sm btn-danger" data-action="archive-performer" data-child="${ch.id}">Archive</button>`}
          </span>` : ''}</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px 24px">
            <div><div class="detail-label">Name</div><div style="font-size:14px;color:var(--cream)">${esc(ch.name)}</div></div>
            <div><div class="detail-label">Date of birth</div><div style="font-size:14px;color:var(--cream)">${ch.dob ? fmtD(ch.dob) : '—'} ${age !== null ? `<span style="color:var(--muted)">(age ${age})</span>` : ''}</div></div>
            <div><div class="detail-label">Gender</div><div style="font-size:14px;color:var(--cream)">${ch.gender ? esc(ch.gender) : '—'}</div></div>
            <div><div class="detail-label">Grade (this fall)</div><div style="font-size:14px;color:var(--cream)">${ch.grade ? esc(ch.grade) : '—'}</div></div>
            <div><div class="detail-label">School (this fall)</div><div style="font-size:14px;color:var(--cream)">${ch.school ? esc(ch.school) : '—'}</div></div>
            <div><div class="detail-label">Account</div><div style="font-size:14px">${acct ? `<button class="tbl-link" onclick="viewParent('${acct.id}')">${esc(acct.firstName + ' ' + acct.lastName)}</button>` : '—'}</div></div>
            <div><div class="detail-label">How they found us</div><div style="font-size:14px;color:var(--cream)">${acct && acct.referral ? esc(acct.referral) : '—'}</div></div>
          </div>
        </div>
      </div>

      <div>
        <div class="card">
          <div class="card-hd">Enrollments ${enrollable && addable.length ? `<button class="btn-ghost btn-sm" data-action="enroll-program" data-child="${ch.id}">+ Enroll in a program</button>` : ''}</div>
          ${enrolledPrograms.length ? enrolledPrograms.map(enrollmentCard).join('') : '<p class="empty">Not enrolled in any programs or classes.</p>'}
          ${enrollable && !addable.length && enrolledPrograms.length ? '<div style="font-size:11px;color:var(--muted);font-style:italic;margin-top:4px">Enrolled in all eligible programs.</div>' : ''}
        </div>
      </div>
    </div>`;
};
VIEW_INIT['a-performer-detail'] = () => {
  document.querySelectorAll('[data-action="edit-child"]').forEach(btn => {
    btn.addEventListener('click', () => childModal(btn.dataset.child));
  });
  document.querySelectorAll('[data-action="enroll-program"]').forEach(btn => {
    btn.addEventListener('click', () => enrollProgramModal(btn.dataset.child));
  });
  document.querySelectorAll('[data-action="billing-switch"]').forEach(btn => {
    btn.addEventListener('click', () => billingSwitchModal(btn.dataset.child, btn.dataset.program));
  });
  document.querySelectorAll('[data-action="drop-program"]').forEach(btn => {
    btn.addEventListener('click', () => dropModal(btn.dataset.child, btn.dataset.program));
  });
  document.querySelectorAll('[data-action="archive-performer"]').forEach(btn => {
    btn.addEventListener('click', () => archivePerformerModal(btn.dataset.child));
  });
  document.querySelectorAll('[data-action="unarchive-performer"]').forEach(btn => {
    btn.addEventListener('click', () => unarchivePerformer(btn.dataset.id));
  });
};
